import { useContext, useEffect, useMemo, useState } from 'react';

import { Delete, Edit } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    ThemeProvider,
    Tooltip,
    MenuItem,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';

import * as UnidadeCondominialAPI from '../../../api/unidadeCondominial';
import { Context } from '../../../context/UserContext';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import { useMRTState } from '../../../hooks/useMRTState';
import { podeCancelar, podeEditar, podeImprimir, podeIncluir } from '../../../hooks/usePermisionMRT';
import { useNavigateParams } from '../../../hooks/useNavigateParams';
import useFlashMessage from '../../../hooks/useFlashMessage';
import { obterPermissoesUsuario } from '../../../utils/funcoes';
import tableThemeCustom from '../../form/tableThemeCustom';

function formatarDataHora(value) {
    if (!value) return '';

    const data = new Date(value);

    return Number.isNaN(data.getTime()) ? '' : data.toLocaleString('pt-BR');
}

function formatarArea(value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const area = Number(value);

    if (!Number.isFinite(area)) {
        return '';
    }

    return area.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
    });
}

function Index() {
    const { parametro } = useContext(Context);
    const { pessoaId, empresaId, empresaIds } = getEmpresaScope();
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigateParams();

    const urlParams = new URLSearchParams(window.location.search);
    const ul = urlParams.get('ul');
    const li = urlParams.get('li');

    const [token] = useState(localStorage.getItem('token') || '');
    const [dados, setDados] = useState([]);
    const [unidadeExcluir, setUnidadeExcluir] = useState(null);
    const [excluindo, setExcluindo] = useState(false);

    const mrt = useMRTState({
        storageKey: 'unidade-condominial.index',
        initialState: {
            columnFilterFns: {},
            columnFilters: [],
            globalFilter: '',
            sorting: [],
            pagination: {
                pageIndex: 0,
                pageSize: parametro?.paginacao || 10,
            },
        },
    });

    const {
        columnFilterFns,
        columnFilters,
        globalFilter,
        sorting,
        pagination,
        setIsLoading,
        setIsError,
        setIsRefetching,
        setRowCount,
    } = mrt;

    const themeMRT = useTheme();
    const isMobile = useMediaQuery(themeMRT.breakpoints.down('sm'));

    const mrtDefault = getMRTDefaultProps({
        mrt,
        parametro,
        ...(isMobile ? { isMobile: true } : {}),
    });

    async function fetchData() {
        dados.length ? setIsRefetching(true) : setIsLoading(true);

        try {
            const response = await UnidadeCondominialAPI.listar({
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: JSON.stringify(columnFilters),
                filtersMode: JSON.stringify(columnFilterFns),
                globalFilter: globalFilter || '',
                sorting: JSON.stringify(sorting),
            });

            setDados(response.data.data || []);
            setRowCount(Number(response.data.meta?.totalRowCount) || 0);
            setIsError(false);
        } catch (error) {
            setIsError(true);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível consultar as unidades condominiais.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setIsLoading(false);
            setIsRefetching(false);
        }
    }

    useEffect(() => {
        if (!token) {
            navigate('/Auth', {
                replace: true,
            });

            return;
        }

        fetchData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, columnFilterFns, columnFilters, globalFilter, pagination.pageIndex, pagination.pageSize, sorting]);

    useEffect(() => {
        if (!pessoaId || !empresaId || !ul || !li) {
            return;
        }

        obterPermissoesUsuario(pessoaId, empresaId, ul, li);
    }, [pessoaId, empresaId, ul, li]);

    function novaUnidade() {
        const params = new URLSearchParams(urlParams);

        params.delete('uuid');
        params.set('btnText', 'Cadastrar');

        navigate('/UnidadesCondominiais/CreateOrEdit', params);
    }

    function editarUnidade(unidade) {
        const params = new URLSearchParams(urlParams);

        params.set('uuid', unidade.uuid);
        params.set('btnText', 'Atualizar');

        navigate('/UnidadesCondominiais/CreateOrEdit', params);
    }

    async function confirmarExclusao() {
        if (!unidadeExcluir?.uuid || excluindo) {
            return;
        }

        setExcluindo(true);

        try {
            const response = await UnidadeCondominialAPI.excluir(unidadeExcluir.uuid);

            setUnidadeExcluir(null);

            setFlashMessage(response.data.message, response.data.type);

            await fetchData();
        } catch (error) {
            setFlashMessage(
                error.response?.data?.message || 'Não foi possível excluir a unidade condominial.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setExcluindo(false);
        }
    }

    const columns = useMemo(
        () => [
            {
                id: 'unidadesCondominiais',
                header: 'UNIDADES CONDOMINIAIS',
                columns: [
                    {
                        accessorKey: 'Empresa.nomeFantasia',
                        header: 'Empresa',
                    },
                    {
                        accessorKey: 'portaria',
                        header: 'Portaria',
                        type: 'boolean',
                        columnFilterModeOptions: ['equals'],
                        filterSelectOptions: [
                            { label: 'Todos', value: '' },
                            { label: 'Sim', value: '1' },
                            { label: 'Não', value: '0' },
                        ],
                        filterVariant: 'select',
                        Cell: ({ row }) => {
                            const value = row.original.portaria;
                            return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                        },
                    },
                    {
                        accessorKey: 'codigo',
                        header: 'Código',
                    },
                    {
                        accessorKey: 'descricao',
                        header: 'Descrição',
                    },
                    {
                        accessorKey: 'quadra',
                        header: 'Quadra',
                        size: 90,
                    },
                    {
                        accessorKey: 'lote',
                        header: 'Lote',
                        size: 90,
                    },
                    {
                        accessorKey: 'area',
                        header: 'Área (m²)',
                        Cell: ({ cell }) => formatarArea(cell.getValue()),
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        type: 'boolean',
                        columnFilterModeOptions: ['equals'],
                        filterSelectOptions: [
                            { label: 'Todos', value: '' },
                            { label: 'Sim', value: '1' },
                            { label: 'Não', value: '0' },
                        ],
                        filterVariant: 'select',
                        Cell: ({ row }) => {
                            const value = row.original.ativo;
                            return <div style={{ textAlign: 'left' }}>{value ? 'Sim' : 'Não'}</div>;
                        },
                    },
                    {
                        accessorKey: 'updatedAt',
                        header: 'Atualizado em',
                        Cell: ({ cell }) => formatarDataHora(cell.getValue()),
                    },
                ],
            },
        ],
        []
    );

    const theme = tableThemeCustom();

    return (
        <div>
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    {...mrtDefault}
                    columns={columns}
                    data={dados}
                    getRowId={(row) => row.uuid}
                    renderTopToolbarCustomActions={({ table }) => {
                        const handleImprimir = async (obj) => {
                            const urlSearchParams = new URLSearchParams({
                                start: `${pagination.pageIndex * pagination.pageSize}`,
                                size: `${pagination.pageSize}`,
                                filters: JSON.stringify(columnFilters ?? []),
                                filtersMode: JSON.stringify(columnFilterFns ?? []),
                                globalFilter: globalFilter ?? '',
                                sorting: JSON.stringify(sorting ?? []),
                                tipo: urlParams.get('tipo') ?? '',
                                empresaId: empresaIds,
                            });

                            const response = await UnidadeCondominialAPI.imprimir(urlSearchParams, obj);

                            // Cria um Blob a partir do objeto do arraybuffer
                            const pdfBlob = new Blob([response.data], {
                                type: 'application/pdf',
                            });

                            // Cria um URL temporário para o Blob
                            const pdfUrl = URL.createObjectURL(pdfBlob);

                            // Use o URL do PDF para abrir em uma nova janela ou realizar outras ações
                            window.open(pdfUrl);
                        };
                        return (
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '8px',
                                }}
                            >
                                <Tooltip arrow placement="top-end" title={`Novo Registro`}>
                                    <Button color="secondary" variant="contained" disabled={!podeIncluir()} onClick={novaUnidade}>
                                        Novo
                                    </Button>
                                </Tooltip>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/UnidadesCondominiais/Cobranca', new URLSearchParams(urlParams))}
                                >
                                    Prévia de cobrança
                                </Button>
                                <Tooltip arrow placement="top-end" title={`Imprimir Unidades Condominiais`}>
                                    <div>
                                        <span>
                                            <Button
                                                color="info"
                                                variant="contained"
                                                disabled={!podeImprimir()}
                                                onClick={() => handleImprimir({})}
                                            >
                                                Imprimir
                                            </Button>
                                        </span>
                                    </div>
                                </Tooltip>
                            </div>
                        );
                    }}
                    renderRowActions={
                        !isMobile
                            ? ({ row }) => (
                                  <Box
                                      sx={{
                                          display: 'flex',
                                          gap: 1,
                                      }}
                                  >
                                      <Tooltip title="Editar unidade condominial">
                                          <span>
                                              <IconButton
                                                  color="primary"
                                                  disabled={!podeEditar()}
                                                  onClick={() => editarUnidade(row.original)}
                                              >
                                                  <Edit />
                                              </IconButton>
                                          </span>
                                      </Tooltip>

                                      <Tooltip title="Excluir unidade condominial">
                                          <span>
                                              <IconButton
                                                  color="error"
                                                  disabled={!podeCancelar()}
                                                  onClick={() => setUnidadeExcluir(row.original)}
                                              >
                                                  <Delete />
                                              </IconButton>
                                          </span>
                                      </Tooltip>
                                  </Box>
                              )
                            : undefined
                    }
                    renderRowActionMenuItems={
                        isMobile
                            ? ({ row, closeMenu }) => [
                                  <MenuItem
                                      key="editar"
                                      disabled={!podeEditar()}
                                      onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          editarUnidade(row.original);
                                          closeMenu();
                                      }}
                                  >
                                      <ListItemIcon>
                                          <Edit color="primary" fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText>Editar</ListItemText>
                                  </MenuItem>,
                                  <MenuItem
                                      key="excluir"
                                      disabled={!podeCancelar()}
                                      onClick={(event) => {
                                          event.preventDefault();
                                          event.stopPropagation();
                                          setUnidadeExcluir(row.original);
                                          closeMenu();
                                      }}
                                  >
                                      <ListItemIcon>
                                          <Delete color="error" fontSize="small" />
                                      </ListItemIcon>
                                      <ListItemText>Excluir</ListItemText>
                                  </MenuItem>,
                              ]
                            : undefined
                    }
                />
            </ThemeProvider>

            <Dialog
                open={Boolean(unidadeExcluir)}
                onClose={() => {
                    if (!excluindo) {
                        setUnidadeExcluir(null);
                    }
                }}
            >
                <DialogTitle>Excluir unidade condominial?</DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        A unidade{' '}
                        <strong>
                            {unidadeExcluir?.codigo} — {unidadeExcluir?.descricao}
                        </strong>{' '}
                        será excluída.
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <Button disabled={excluindo} onClick={() => setUnidadeExcluir(null)}>
                        Cancelar
                    </Button>

                    <Button variant="contained" color="error" disabled={excluindo} onClick={confirmarExclusao}>
                        {excluindo ? 'Excluindo...' : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default Index;
